// The signed-in user's assistant (fullstack CLERK.md § Assistants: a separate account, linked):
// another person's account, linked by the server (privateMetadata, which the browser can't
// read), so it's read and changed through the fullstack's api/v1/user/assistant/*. Shared by the
// email editor (EmailEditor, useEmailEdit) and the Add Assistant dialog (EmailChange).

import {ref} from "vue";
import {authFetch} from "@/composables/useAuthFetch";

// the email editor's row for the assistant, marked for removal like an address (useEmailEdit)
export const ASSISTANT_ROW_ID = "assistant";
const assistant = ref(null); // {email, source, at} or null
// the account access given to someone (approved from a payment for this account), {name, email,
// opensUntil} or null: the editor's "End Access"
const access = ref(null);
const isAssistantLoaded = ref(false);

async function getResult(response) {
  const data = await response.json().catch(() => ({}));
  if (response.ok) return {ok: true, ...data};
  const fallback = response.status === 401 ? "Please sign in again." : "That didn't work. "
    + "Please try again.";
  return {ok: false, message: data.statusMessage || fallback};
}

async function postAssistantAction(action, body = {}) {
  const response = await authFetch(`/api/v1/user/assistant/${action}`, {
    method: "POST",
    headers: {"Content-Type": "application/json"},
    body: JSON.stringify(body),
  });
  return await getResult(response);
}

export async function fetchAssistant() {
  const result = await getResult(await authFetch("/api/v1/user/assistant"));
  if (result.ok) assistant.value = result.assistant || null;
  if (result.ok) access.value = result.access || null;
  isAssistantLoaded.value = true;
  return result;
}

// as Settings opens (Profile/Email.vue): nothing shown until it's known (another
// account may have been signed in before, in this tab)
export async function loadAssistant() {
  isAssistantLoaded.value = false;
  assistant.value = null;
  access.value = null;
  return await fetchAssistant();
}

export async function setAssistant(email) {
  const result = await postAssistantAction("set", {email});
  if (result.ok) await fetchAssistant();
  return result;
}

export async function removeAssistant() {
  const result = await postAssistantAction("remove");
  if (result.ok) assistant.value = null;
  return result;
}

// the access cleared and this account's other sessions signed out (the assistant's among them)
export async function endAccess() {
  const result = await postAssistantAction("end-access");
  if (result.ok) access.value = null;
  return result;
}

export function useAssistant() {
  return {assistant, access, isAssistantLoaded};
}
